import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import {
  demoEmail,
  type DemoFixtureIdentity,
  type PslfDemoScenarioId,
} from 'tio-employee/demo/types';

// Stable ids let the handler rewrite requested employee/person ids while the
// sideloaded PSLF graph and identity-derived borrower names stay coherent.

const FEIN = '12-3456789';

// This builder returns plain content rather than JSON:API; balances are dollars.
const LOAN_SUMMARY = {
  summaryByLoanType: [
    {
      loanType: 'direct',
      loanIds: ['demo-loan-1', 'demo-loan-2'],
      currentBalance: 41860,
      interestRate: 5.47,
      monthlyPayment: 453.67,
      repaymentPlan: 'ibr',
      repaymentPlans: ['ibr'],
      totalDisbursedAmount: 47720,
      federalLoanTime: '120',
    },
  ],
  errors: [],
};

function buildEmployeeDoc(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiDoc {
  const { firstName, lastName, employerName } = identity;
  const forms = buildPslfFormResources(scenarioId, identity);
  const formRefs = forms.map(({ type, id }) => ({ type, id }));
  return {
    data: {
      type: 'employee',
      id: 'demo-employee-1',
      attributes: {
        firstName,
        lastName,
        email: demoEmail(identity),
        isActive: true,
        title: 'Registered Nurse',
        employmentStartDate: '2022-01-15',
        customAttributes: { fein: FEIN },
      },
      relationships: {
        pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
        company: { data: { type: 'company', id: 'demo-company-1' } },
        employeeAddition: { data: { type: 'employee-addition', id: 'demo-emp-add-1' } },
        pslfForms: { data: formRefs },
      },
    },
    included: [
      {
        type: 'pslf-profile',
        id: 'demo-pslf-profile-1',
        attributes: { isInterested: true, signaturePresent: true },
        relationships: {
          employee: { data: { type: 'employee', id: 'demo-employee-1' } },
          pslfForms: { data: formRefs },
        },
      },
      {
        type: 'company',
        id: 'demo-company-1',
        attributes: {
          name: employerName,
          companyType: 'partner',
          isActive: true,
          programTitle: 'PSLF Program',
        },
        relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: 'demo-pslf-group-1' }] } },
      },
      {
        type: 'employee-addition',
        id: 'demo-emp-add-1',
        attributes: { processed: { merged: { fein: FEIN } } },
      },
      ...forms,
    ],
  };
}

function buildCompanyDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { employerName } = identity;
  return {
    data: {
      type: 'company',
      id: 'demo-company-1',
      attributes: {
        name: employerName,
        companyType: 'partner',
        isActive: true,
        programTitle: 'PSLF Program',
      },
      relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: 'demo-pslf-group-1' }] } },
    },
    included: [
      {
        type: 'pslf-group',
        id: 'demo-pslf-group-1',
        attributes: {
          approverEmail: 'hr@example.com',
          approverName: 'Sarah Johnson',
          approverTitle: 'HR Director',
          employerCommonName: employerName,
          employerLegalName: `${employerName} Inc.`,
          employerFein: FEIN,
          employerIsNotForProfit: true,
          employerCity: 'Portland',
          employerState: 'OR',
          employerPostalCode: '97205',
        },
        relationships: { company: { data: { type: 'company', id: 'demo-company-1' } } },
      },
    ],
  };
}

// Inline PDF (data URI) for the demo form's attachment — a real, minimal PDF so
// the form/success route's preview renders client-side instead of fetching a
// signed-S3 url the network-free block would stop.
const DEMO_PSLF_FORM_PDF =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iajw8L1R5cGUvQ2F0YWxvZy9QYWdlcyAyIDAgUj4+ZW5kb2JqCjIgMCBvYmo8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PmVuZG9iagozIDAgb2JqPDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgMzAwIDIwMF0vUmVzb3VyY2VzPDwvRm9udDw8L0YxIDQgMCBSPj4+Pi9Db250ZW50cyA1IDAgUj4+ZW5kb2JqCjQgMCBvYmo8PC9UeXBlL0ZvbnQvU3VidHlwZS9UeXBlMS9CYXNlRm9udC9IZWx2ZXRpY2E+PmVuZG9iago1IDAgb2JqPDwvTGVuZ3RoIDYyPj5zdHJlYW0KQlQgL0YxIDE0IFRmIDQwIDEyMCBUZCAoUFNMRiBGb3JtIC0gRGVtbyBEb2N1bWVudCkgVGogRVQKZW5kc3RyZWFtIGVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCnRyYWlsZXI8PC9Sb290IDEgMCBSL1NpemUgNj4+CnN0YXJ0eHJlZgowCiUlRU9G';

const PSLF_FORM_ATTACHMENT: JsonApiResource = {
  type: 'attachment',
  id: 'demo-pslf-attachment-1',
  attributes: {
    filename: 'pslf-form-demo.pdf',
    contentType: 'application/pdf',
    signedUrl: DEMO_PSLF_FORM_PDF,
  },
};

interface PslfFormFixture {
  id: string;
  status: string;
  formType: 'currentEmployer' | 'priorEmployer';
  employerName?: string;
  employmentStartDate: string;
  employmentEndDate?: string;
  createdAt: string;
  borrowerCompletedAt: string;
  employerCompletedAt?: string;
  submittedAt?: string;
  employerRejectedAt?: string;
}

const SUBMITTED_CURRENT_FORM: PslfFormFixture = {
  id: 'demo-form-1',
  status: 'submitted',
  formType: 'currentEmployer',
  employmentStartDate: '2022-01-15',
  createdAt: '2026-05-16T09:00:00.000Z',
  borrowerCompletedAt: '2026-05-17',
  employerCompletedAt: '2026-05-21',
  submittedAt: '2026-05-22',
};

const MULTI_FORM_JOURNEY: PslfFormFixture[] = [
  SUBMITTED_CURRENT_FORM,
  {
    id: 'demo-form-2',
    status: 'submitted',
    formType: 'priorEmployer',
    employerName: 'City General Hospital',
    employmentStartDate: '2019-06-01',
    employmentEndDate: '2021-12-31',
    createdAt: '2026-05-04T09:00:00.000Z',
    borrowerCompletedAt: '2026-05-06',
    employerCompletedAt: '2026-05-11',
    submittedAt: '2026-05-12',
  },
  {
    id: 'demo-form-3',
    status: 'borrower_signed',
    formType: 'priorEmployer',
    employerName: 'Riverside Community College',
    employmentStartDate: '2017-08-15',
    employmentEndDate: '2019-05-31',
    createdAt: '2026-05-27T09:00:00.000Z',
    borrowerCompletedAt: '2026-05-29',
  },
];

const PSLF_FORMS_BY_SCENARIO = {
  'blank-slate': [],
  'borrower-signed': [
    {
      ...SUBMITTED_CURRENT_FORM,
      id: 'demo-form-borrower-signed',
      status: 'borrower_signed',
      employerCompletedAt: undefined,
      submittedAt: undefined,
    },
  ],
  'employer-signed': [
    {
      ...SUBMITTED_CURRENT_FORM,
      id: 'demo-form-employer-signed',
      status: 'awaiting_submit',
      submittedAt: undefined,
    },
  ],
  submitted: [SUBMITTED_CURRENT_FORM],
  'form-rejected': [
    {
      ...SUBMITTED_CURRENT_FORM,
      id: 'demo-form-rejected',
      status: 'rejected',
      submittedAt: undefined,
      employerRejectedAt: '2026-06-03',
    },
  ],
  'multi-form': MULTI_FORM_JOURNEY,
  'with-nslds': MULTI_FORM_JOURNEY,
  'full-journey': MULTI_FORM_JOURNEY,
} satisfies Record<PslfDemoScenarioId, PslfFormFixture[]>;

function buildPslfFormResource(
  fixture: PslfFormFixture,
  identity: DemoFixtureIdentity
): JsonApiResource {
  const { firstName, lastName } = identity;
  const employerName = fixture.employerName ?? identity.employerName;
  const employerSigned = fixture.status !== 'borrower_signed';
  return {
    type: 'pslf-form',
    id: fixture.id,
    attributes: {
      status: fixture.status,
      formType: fixture.formType,
      createdAt: fixture.createdAt,
      borrowerCompletedAt: fixture.borrowerCompletedAt,
      employerCompletedAt: fixture.employerCompletedAt ?? null,
      submittedAt: fixture.submittedAt ?? null,
      ...(fixture.employerRejectedAt ? { employerRejectedAt: fixture.employerRejectedAt } : {}),
      isNonPartner: false,
      borrowerFields: {
        'borrower-name': `${firstName} ${lastName}`,
        'borrower-email': demoEmail(identity),
        'borrower-employer-name': employerName,
        'borrower-employment-start-date': fixture.employmentStartDate,
        'borrower-employment-end-date': fixture.employmentEndDate ?? null,
        'borrower-employment-status': 'employed',
        'borrower-employment-continues': fixture.formType === 'currentEmployer',
        'borrower-employer-ein': FEIN,
      },
      employerFields: employerSigned
        ? {
            'employer-name': employerName,
            'employer-fein': FEIN,
            'employer-city': 'Portland',
            'employer-state': 'OR',
            'employer-zip': '97205',
            'employer-email': 'hr@example.com',
          }
        : {},
    },
    relationships: {
      pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
      attachments: { data: [{ type: 'attachment', id: 'demo-pslf-attachment-1' }] },
    },
  };
}

function buildPslfFormResources(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiResource[] {
  return PSLF_FORMS_BY_SCENARIO[scenarioId].map((fixture) =>
    buildPslfFormResource(fixture, identity)
  );
}

function buildPslfFormCollection(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiDoc {
  const forms = buildPslfFormResources(scenarioId, identity);
  return {
    data: forms,
    ...(forms.length ? { included: [PSLF_FORM_ATTACHMENT] } : {}),
    meta: { page: { total: forms.length } },
  };
}

// The form/success route reads a SINGLE pslf-form by id (findRecord, include:
// attachments). The id comes from a dashboard link or the just-submitted form's
// client id, so echo it — mirroring the collection form + its attachment.
function buildPslfFormSingle(
  url: string,
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiDoc {
  const id = /\/pslf-forms\/([^/?]+)/.exec(url)?.[1] ?? 'demo-form-1';
  const fixture =
    PSLF_FORMS_BY_SCENARIO[scenarioId].find((candidate) => candidate.id === id) ??
    ({ ...SUBMITTED_CURRENT_FORM, id } satisfies PslfFormFixture);
  return {
    data: buildPslfFormResource(fixture, identity),
    included: [PSLF_FORM_ATTACHMENT],
  };
}

// Plan-summary money is cents; createdAt orders the combined history.
const RECOMMENDATION_ATTRS = {
  hasErrorInCalcs: false,
  bestPlanSummary: {
    // Reconciles on screen: payoff = monthly x term, and
    // forgiven = balance + interest accrued - amount actually paid.
    'payoff-amount': [2472007],
    'term-months': [71],
    'monthly-payments': [34817],
    'total-interest-paid': [1354764],
    'estimated-amount-forgiven': [3068757],
  },
  currentPlanSummary: {
    // Standard 10-year on the $41,860 balance at 5.47%: payoff = monthly x
    // 120 AND = principal + interest, since nothing is forgiven.
    'payoff-amount': [5444040],
    'term-months': [120],
    'monthly-payments': [45367],
    'total-interest-paid': [1258040],
    'estimated-amount-forgiven': [0],
  },
  bestPlanNextSteps: [],
  bestPlanTableHeadings: [],
  currentPlanTableHeadings: [],
  inputSummary: {},
  outputSummary: {},
};

const RECOMMENDATION_0726_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'recommendation',
      id: 'demo-recommendation-0726-1',
      attributes: {
        ...RECOMMENDATION_ATTRS,
        requestingApp: 'PSLF-0726',
        createdAt: '2026-06-15T14:00:00.000Z',
      },
    },
  ],
  meta: { page: { total: 1 } },
};

const RECOMMENDATION_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'recommendation',
      id: 'demo-recommendation-1',
      attributes: {
        ...RECOMMENDATION_ATTRS,
        requestingApp: 'PSLF',
        createdAt: '2026-05-20T14:00:00.000Z',
      },
    },
  ],
  meta: { page: { total: 1 } },
};

const EMPTY_COLLECTION: JsonApiDoc = { data: [], meta: { page: { total: 0 } } };

function recommendationCollection(
  scenarioId: PslfDemoScenarioId,
  collection: JsonApiDoc
): JsonApiDoc {
  return scenarioId === 'full-journey' ? collection : EMPTY_COLLECTION;
}

// A non-empty profile prevents the dashboard from attempting a blocked create.
const ANSWER_PROFILE_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'answer-profile',
      id: 'demo-answer-profile-1',
      attributes: {
        consolidatedLoansAt: null,
        changedRepaymentPlanAt: null,
        recertifiedIncomeAt: null,
      },
    },
  ],
};

// My Documents reads meta.page.total even for an empty collection.
const EMPTY_OBSERVABLE_DOCUMENTS: JsonApiDoc = { data: [], meta: { page: { total: 0 } } };

// A single-recommendation read for the estimator RESULTS route
// (`findRecord('recommendation', id)` — reached by clicking a dashboard history
// row). The id comes from the fixture history (outside a collection body), so
// echo it into the returned resource, mirroring the request handler's op-path
// `withEchoedId`.
function buildRecommendationEcho(url: string): JsonApiDoc {
  const id = /\/recommendations\/([^/?]+)/.exec(url)?.[1] ?? 'demo-recommendation-0726-1';
  return {
    data: {
      type: 'recommendation',
      id,
      attributes: {
        ...RECOMMENDATION_ATTRS,
        requestingApp: 'PSLF-0726',
        createdAt: '2026-06-15T14:00:00.000Z',
      },
    },
  };
}

const isRecommendationCollection = (url: string) =>
  url.includes('/recommendations') && url.includes('requesting-app');
// Match the EXACT requesting-app value, not a prefix: 'PSLF' must not swallow
// 'PSLF-0726', and neither may serve another product's recommendation query
// (e.g. IDR) — that would hide that product's fixture gap. `[^=]*` skips the
// encoded/plain `]` between the filter key and `=`.
const requestingAppIs = (url: string, app: string) => {
  // Escape regex metacharacters — the current callers pass safe constants, but
  // keep the generic helper robust if an app id ever contains them.
  const escaped = app.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`requesting-app[^=]*=${escaped}(?:&|$)`).test(url);
};
const isSingleResource = (segment: string) => (url: string) =>
  new RegExp(`/${segment}/[^/?]+(?:\\?|$)`).test(url);
const isRecommendationSingle = isSingleResource('recommendations');
const isPslfFormSingle = isSingleResource('pslf-forms');

/**
 * Build raw-fetch fixtures for an authored PSLF journey.
 * @param scenarioId selects the form and estimator state
 * @param identity supplies borrower and employer values
 * @returns ordered fetch matchers for PSLF builder requests
 */
export function buildPslfFetchMatchers(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): FetchMatcher[] {
  return [
    // getLoanSummaryRequest → GET /users/<id>/summarize-loans (plain LoanSummary).
    { urlIncludes: 'summarize-loans', method: 'GET', json: LOAN_SUMMARY },
    // recommendation queries — matched on the EXACT requesting-app so the two
    // dashboard queries get distinct rows (PSLF-0726 + legacy PSLF) and other
    // products' recommendation queries (e.g. IDR) fall through to the block
    // instead of being served PSLF data.
    {
      urlMatches: (url) => isRecommendationCollection(url) && requestingAppIs(url, 'PSLF-0726'),
      method: 'GET',
      json: recommendationCollection(scenarioId, RECOMMENDATION_0726_COLLECTION),
    },
    {
      urlMatches: (url) => isRecommendationCollection(url) && requestingAppIs(url, 'PSLF'),
      method: 'GET',
      json: recommendationCollection(scenarioId, RECOMMENDATION_COLLECTION),
    },
    // Single recommendation (results route).
    { urlMatches: isRecommendationSingle, method: 'GET', json: buildRecommendationEcho },
    // pslf-form: the single read (form/success route, findRecord) is matched
    // FIRST so it gets a single-resource body; the collection query (dashboard
    // filter[pslf-profile]; drawer filter[employee-id]) is the fallback.
    {
      urlMatches: isPslfFormSingle,
      method: 'GET',
      json: (url: string) => buildPslfFormSingle(url, scenarioId, identity),
    },
    {
      urlMatches: (url) => url.includes('/pslf-forms') && !isPslfFormSingle(url),
      method: 'GET',
      json: buildPslfFormCollection(scenarioId, identity),
    },
    // Shared "My Documents" widget — coherent empty page.
    { urlIncludes: '/observable-documents', method: 'GET', json: EMPTY_OBSERVABLE_DOCUMENTS },
    // answer-profile findOrCreate (dash-recommendations, on render): a non-empty
    // query result short-circuits the createRecord (mutations are blocked in demo).
    { urlIncludes: '/answer-profiles', method: 'GET', json: ANSWER_PROFILE_COLLECTION },
  ];
}

/**
 * Build request-handler fixtures for an authored PSLF journey.
 * @param scenarioId selects the form and estimator state
 * @param identity supplies borrower and employer values
 * @returns ordered operation matchers for legacy store requests
 */
export function buildPslfMatchers(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): MatcherFixture[] {
  return [
    { matcher: { kind: 'builder', urlIncludes: 'summarize-loans' }, content: LOAN_SUMMARY },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'employee' },
      docs: [buildEmployeeDoc(scenarioId, identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'company' },
      docs: [buildCompanyDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'pslf-form' },
      docs: [buildPslfFormCollection(scenarioId, identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'recommendation' },
      docs: [recommendationCollection(scenarioId, RECOMMENDATION_COLLECTION)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'answer-profile' },
      docs: [ANSWER_PROFILE_COLLECTION],
    },
  ];
}
